/**
 * DriverTripDetailPage — DSL-driven trip detail for driver.
 *
 * Detail sections, status flow, and action buttons are all driven
 * by DSL config from `src/dsl/driver/`.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePageParams } from '@/hooks/usePageParams';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ChevronLeft, Play, CheckCircle2,
  AlertTriangle, Camera, MapPin, Clock,
  CircleDot, Circle, Square, RefreshCw,
} from 'lucide-react';
import { TRIP_DETAIL_DSL } from '@/dsl/driver/driver-trip-detail.dsl';
import {
  TRIP_STATUS_COLORS,
  TRIP_TYPE_LABELS,
  STOP_STATUS_STYLES,
  getNextAction,
  isAtDeliveryPhase,
} from '@/dsl/driver/driver-config';
import { Gen5LayerStatusCard, type Gen5LayerStatusEnvelope } from '@boox/ui-foundation'; // P1-4
import { ComplianceField, ComplianceLegend, ComplianceScoreBadge, useCompliance } from '@/components/compliance';
import { OpsChecklistPanel } from '@/components/checklist/OpsChecklistPanel';

// TR128 compliance-certified data fields (SS 626:2024)
const TR128_FIELDS = new Set(['vehicleNo', 'scheduledDate']);

// ─── Types ───────────────────────────────────────────────────────────────────

interface TripStop {
  id: string;
  trip_id: string;
  stop_seq: number;
  stop_type: string;
  stop_status: string;
  location_name: string | null;
  address: string | null;
  scheduled_arrival: string | null;
}

interface SiblingLeg {
  id: string;
  trip_type: string;
  trip_role: string;
  sequence_no: number;
  status: string;
  execution_status: string;
}

interface TripDetail {
  tripId: string;
  tripNo: string;
  tripType: string;
  tripRole: string;
  tripStatus: string;
  dispatchStatus: string;
  scheduledDate: string;
  vehicleNo: string | null;
  jobId: string;
  jobNo: string;
  jobType: string;
  serviceMode: string | null;
  customerName: string | null;
  stops: TripStop[];
  siblingLegs: SiblingLeg[];
}

// ─── Stop icon mapping ───────────────────────────────────────────────────────

const STOP_ICON_MAP: Record<string, typeof Circle> = {
  circle:        Circle,
  'circle-dot':  CircleDot,
  'check-circle': CheckCircle2,
  square:        Square,
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function DriverTripDetailPage() {
  const { tripId } = usePageParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [trip, setTrip] = useState<TripDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTrip = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setError(null);
    try {
      // Use dedicated trip detail endpoint (not list→find)
      const resp = await api.get<{
        tripId: string; tripType: string; tripRole: string;
        sequenceNo: number; status: string; executionStatus: string;
        dispatchStatus: string; vehicle: string | null;
        scheduledDate: string; jobId: string; jobNo: string;
        jobType: string; serviceMode: string | null;
        customerName: string | null; origin: string | null;
        destination: string | null;
        stops: TripStop[]; siblingLegs: SiblingLeg[];
      }>(`/tms/mobile/trips/${tripId}`);
      setTrip({
        tripId: resp.tripId,
        tripNo: resp.jobNo ? `${resp.jobNo}-L${resp.sequenceNo}` : resp.tripId,
        tripType: resp.tripType,
        tripRole: resp.tripRole,
        tripStatus: resp.status,
        dispatchStatus: resp.dispatchStatus,
        scheduledDate: resp.scheduledDate,
        vehicleNo: resp.vehicle,
        jobId: resp.jobId,
        jobNo: resp.jobNo,
        jobType: resp.jobType,
        serviceMode: resp.serviceMode,
        customerName: resp.customerName,
        stops: resp.stops ?? [],
        siblingLegs: resp.siblingLegs ?? [],
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load trip');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { void loadTrip(); }, [loadTrip]);

  // ── Advance trip status (DSL-driven) ──
  const advanceStatus = useCallback(async () => {
    if (!trip) return;
    const action = getNextAction(trip.tripStatus);
    if (!action) return;

    setUpdating(true);
    try {
      let lat: number | undefined;
      let lng: number | undefined;
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS unavailable */ }

      await api.post(`/tms/mobile/trips/${trip.tripId}/update-status`, {
        status: action.nextStatus,
        lat,
        lng,
      });
      await loadTrip();
    } catch (err: unknown) {
      if (err instanceof Error && 'code' in err && (err as { code: string }).code === 'OFFLINE_QUEUED') {
        setError('⏳ 已离线保存，恢复网络后将自动提交');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update status');
      }
    } finally {
      setUpdating(false);
    }
  }, [trip, loadTrip]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16">
        <AlertTriangle className="h-8 w-8 text-destructive opacity-50 mb-2" />
        <p className="text-sm text-muted-foreground">{error ?? 'Trip not found'}</p>
        <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate('/driver/trips')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>
    );
  }

  const isCompleted = trip.tripStatus === 'COMPLETED' || trip.tripStatus === 'CANCELLED';
  const nextAction = getNextAction(trip.tripStatus);
  const atDelivery = isAtDeliveryPhase(trip.tripStatus);
  const compliance = useCompliance(trip.jobId);

  // Build trip data record for DSL field rendering
  const tripData: Record<string, unknown> = {
    ...trip,
    scheduledDate: trip.scheduledDate ? new Date(trip.scheduledDate).toLocaleDateString() : '—',
  };

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => navigate('/driver/trips')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold truncate">{trip.tripNo || 'Trip'}</h1>
            <p className="text-xs text-muted-foreground truncate">
              {TRIP_TYPE_LABELS[trip.tripType] ?? trip.tripType} · {trip.jobNo}              {trip.serviceMode && (
                <span className="ml-1 text-blue-600">
                  ({trip.serviceMode === 'ROUND_TRIP' ? 'Round Trip' :
                    trip.serviceMode === 'STREET_TURN' ? 'Street Turn' :
                    trip.serviceMode === 'ONE_WAY' ? 'One Way' : trip.serviceMode})
                </span>
              )}            </p>
          </div>
          <Badge className={`text-[10px] ${TRIP_STATUS_COLORS[trip.tripStatus] ?? 'bg-gray-100'}`}>
            {trip.tripStatus.replace(/_/g, ' ')}
          </Badge>
          <ComplianceScoreBadge score={compliance.data.overallScore} />
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-auto px-4 py-3 space-y-3">

        {/* Trip info card — rendered from DSL sections */}
        <Card>
          <CardContent className="p-3 space-y-2">
            {(TRIP_DETAIL_DSL.sections ?? []).map((section) => (
              <div key={section.title}>
                <div className={`grid grid-cols-${section.columns ?? 2} gap-2 text-xs`}>
                  {section.fields.map((fieldDef) => (
                    <div key={fieldDef.field} style={{ gridColumn: fieldDef.span ? `span ${fieldDef.span}` : undefined }}>
                      {TR128_FIELDS.has(fieldDef.field) ? (
                        <ComplianceField>
                          <span className="text-muted-foreground">{fieldDef.label ?? fieldDef.field}</span>
                          <p className="font-medium truncate">{String(tripData[fieldDef.field] ?? '—')}</p>
                        </ComplianceField>
                      ) : (
                        <>
                          <span className="text-muted-foreground">{fieldDef.label ?? fieldDef.field}</span>
                          <p className="font-medium truncate">{String(tripData[fieldDef.field] ?? '—')}</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <ComplianceLegend compact />
          </CardContent>
        </Card>

        {/* Compliance quick status */}
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">Compliance</span>
              <ComplianceScoreBadge score={compliance.data.overallScore} size="md" />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {compliance.data.domains.filter(d => ['pod', 'eir', 'vgm', 'gps', 'documents', 'milestones'].includes(d.domain)).map(d => (
                <div key={d.domain} className="text-center">
                  <div className={`text-xs font-bold ${d.status === 'pass' ? 'text-green-600' : d.status === 'fail' ? 'text-red-600' : 'text-amber-600'}`}>
                    {d.score}%
                  </div>
                  <div className="text-[9px] text-muted-foreground">{d.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trucking checklist capability — template-driven by trip type/status */}
        <OpsChecklistPanel
          tripType={trip.tripType}
          serviceMode={trip.serviceMode}
          currentStatus={trip.tripStatus}
          compact
        />

        {/* Stop timeline */}
        <Card>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm">{t('driver.stops', 'Stops')} ({trip.stops.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            {trip.stops.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No stops configured</p>
            ) : (
              <div className="space-y-0">
                {trip.stops.map((stop, idx) => {
                  const style = STOP_STATUS_STYLES[stop.stop_status] ?? STOP_STATUS_STYLES.PENDING ?? { icon: 'circle', color: 'text-muted-foreground' };
                  const StopIcon = STOP_ICON_MAP[style.icon] ?? Circle;
                  const isLast = idx === trip.stops.length - 1;
                  return (
                    <div key={stop.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <StopIcon className={`h-4 w-4 shrink-0 ${style.color}`} />
                        {!isLast && <div className="w-px flex-1 bg-border min-h-[24px]" />}
                      </div>
                      <div className="pb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium">
                            {stop.stop_type.replace(/_/g, ' ')}
                          </span>
                          <Badge variant="outline" className="text-[9px] px-1">
                            {stop.stop_status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          <MapPin className="inline h-3 w-3 mr-0.5" />
                          {stop.location_name ?? stop.address ?? '—'}
                        </p>
                        {stop.scheduled_arrival && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            <Clock className="inline h-3 w-3 mr-0.5" />
                            {new Date(stop.scheduled_arrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sibling legs (round-trip awareness) */}
        {trip.siblingLegs.length > 0 && (
          <Card>
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm">
                {t('driver.otherLegs', 'Other Legs')} ({trip.siblingLegs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 space-y-2">
              {trip.siblingLegs.map((leg) => {
                const isDone = ['completed', 'COMPLETED'].includes(leg.status);
                return (
                  <div key={leg.id} className={`flex items-center justify-between p-2 rounded border text-xs ${isDone ? 'bg-green-50/50 border-green-200' : 'bg-muted/20'}`}>
                    <div>
                      <span className="font-medium">
                        Leg {leg.sequence_no}: {TRIP_TYPE_LABELS[leg.trip_type] ?? leg.trip_type}
                      </span>
                      <span className="ml-2 text-muted-foreground">{leg.trip_role}</span>
                    </div>
                    <Badge variant="outline" className={`text-[9px] ${isDone ? 'border-green-300 text-green-700' : ''}`}>
                      {leg.status}
                    </Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Bottom action bar (DSL-driven from statusActions) ── */}
      {!isCompleted && (
        <div className="sticky bottom-0 z-10 bg-background border-t px-4 py-3 space-y-2">
          {nextAction && (
            <Button className="w-full" onClick={advanceStatus} disabled={updating}>
              {updating ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {nextAction.label}
            </Button>
          )}

          <div className="flex gap-2">
            {atDelivery && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/driver/deliver/${trip.tripId}`)}
              >
                <Camera className="h-4 w-4 mr-1" />
                {t('driver.deliveryProcess', 'Delivery Process')}
              </Button>
            )}
            <Button
              variant="outline"
              className={atDelivery ? 'flex-1' : 'w-full'}
              onClick={() => navigate(`/driver/deliver/${trip.tripId}?mode=exception`)}
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              {t('driver.reportIssue', 'Report Issue')}
            </Button>
          </div>

          {/* P1-4: Gen5 Layer Status Card */}
          {(() => {
            const runId = `trip:driver:${trip.tripId}:${trip.tripStatus}`;
            const envelope: Gen5LayerStatusEnvelope = {
              runId,
              taskCategory: 'driver_trip_agent',
              riskLevel: trip.tripStatus === 'CANCELLED' ? 'medium' : 'low',
              executionMode: 'autonomous',
              layers: {
                context: {
                  label: 'Trip Context',
                  status: trip.jobNo ? 'ready' : 'partial',
                  summary: trip.tripNo || trip.tripId,
                  confidence: trip.jobNo ? 0.92 : 0.7,
                },
                intelligence: {
                  label: 'Route Intel',
                  status: trip.stops.length > 0 ? 'ready' : 'partial',
                  summary: trip.stops.length > 0 ? `${trip.stops.length} stops` : 'no stops',
                  confidence: trip.stops.length > 0 ? 0.88 : 0.6,
                },
                harness: {
                  label: 'Execution',
                  status: isCompleted ? 'ready' : 'waiting',
                  summary: trip.tripStatus.replace(/_/g, ' ').toLowerCase(),
                  confidence: isCompleted ? 0.95 : 0.8,
                },
              },
            };
            return <Gen5LayerStatusCard envelope={envelope} compact className="mt-1" />;
          })()}
        </div>
      )}
    </div>
  );
}
